import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { getTCGdexEndpoint } from '../utils/setsByYear';
import { findCard, searchLocalByNumber } from '../utils/cardLookup';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';
import CameraService from '../services/CameraService';
import { titanIdentify } from '../services/titanIdentify';
import AddParentModal from '../components/AddParentModal';

// Chase character icon
import ChaseHi from '../assets/images/ChaseHi.png';

// ============================================
// SCANNER PAGE - Full flow card scanner
// ============================================

// Scanner phases
const PHASE = {
  CAMERA: 'camera',        // Live camera view
  PICKING: 'picking',      // Opening photo picker (prevents camera restart)
  FROZEN: 'frozen',        // Frozen frame, processing capture
  PICK_CROP: 'pick_crop',  // Crop library image with frame guide
  CROP: 'crop',            // Adjust crop if needed
  ANALYZING: 'analyzing',  // AI analyzing the card
  CANDIDATES: 'candidates', // Show matched cards
  NOT_FOUND: 'not_found',  // Card not found - search or manual
  SEARCH: 'search',        // Manual search
  DETAILS: 'details',      // Variant & condition selection
};

// Grade/condition options
const RAW_CONDITIONS = ['NM', 'LP', 'MP', 'HP', 'DMG'];
const PSA_GRADES = [
  { value: 'PSA 10', label: 'PSA 10 - Gem Mint' },
  { value: 'PSA 9', label: 'PSA 9 - Mint' },
  { value: 'PSA 8', label: 'PSA 8 - NM-MT' },
  { value: 'PSA 7', label: 'PSA 7 - NM' },
  { value: 'PSA 6', label: 'PSA 6 - EX-MT' },
  { value: 'PSA 5', label: 'PSA 5 - EX' },
];
const CGC_GRADES = [
  { value: 'CGC 10', label: 'CGC 10 - Pristine' },
  { value: 'CGC 9.5', label: 'CGC 9.5 - Gem Mint' },
  { value: 'CGC 9', label: 'CGC 9 - Mint' },
  { value: 'CGC 8.5', label: 'CGC 8.5 - NM-MT+' },
  { value: 'CGC 8', label: 'CGC 8 - NM-MT' },
];
const BGS_GRADES = [
  { value: 'BGS 10', label: 'BGS 10 - Pristine' },
  { value: 'BGS 9.5', label: 'BGS 9.5 - Gem Mint' },
  { value: 'BGS 9', label: 'BGS 9 - Mint' },
  { value: 'BGS 8.5', label: 'BGS 8.5 - NM-MT+' },
  { value: 'BGS 8', label: 'BGS 8 - NM-MT' },
];

// Gemini API (replaced by Titan Speed Scan)
// const GEMINI_API_KEY = 'AIzaSyAfcHjN75Fp1VKC1vUe2Jp2kUGynxZxQQk';

// Gemini metadata & prompt (replaced by Titan Speed Scan)
/* const CARD_METADATA = `
## POKEMON CARD IDENTIFICATION GUIDE

### ERA IDENTIFICATION (check in order):

1. REGULATION MARK (bottom-left letter in box):
   - D: Sword & Shield 2020-2021 (Base through Vivid Voltage)
   - E: Sword & Shield 2021-2022 (Battle Styles through Fusion Strike)
   - F: Sword & Shield 2022-2023 (Brilliant Stars through Crown Zenith)
   - G: Scarlet & Violet 2023-2024 (Base through Obsidian Flames)
   - H: Scarlet & Violet 2024+ (Paldea Evolved onwards)
   - None: Pre-2020 card

2. BORDER COLOR:
   - Yellow: Base/Neo era (1999-2002)
   - Silver: EX era (2003-2007)
   - Grey: BW/XY/SM (2011-2019)
   - Black: SWSH/SV (2020+)

3. DOT CODE STRIP (barcode at bottom): E-Card era (2002-2003) - Expedition, Aquapolis, Skyridge

4. FIRST EDITION STAMP: Base through Neo era (1999-2002)

### SUFFIX IDENTIFICATION:

| Suffix | Era | Visual Tell |
|--------|-----|-------------|
| EX (uppercase) | 2003-2007 OR 2014-2016 | Silver border (old) or grey border (XY) |
| ex (lowercase) | 2023+ | Black border, regulation mark G/H |
| GX | 2017-2019 | Sun & Moon, GX attack, stylized name |
| V | 2020-2023 | Sword & Shield, black border, reg mark D-F |
| VMAX | 2020-2023 | Oversized art, 300+ HP, Gigantamax |
| VSTAR | 2022-2023 | Star design, VSTAR Power ability |
| TAG TEAM | 2019 | Two Pokemon names + GX |
| Prime | 2010-2011 | HGSS era, stylized holographic |
| LV.X | 2007-2009 | Diamond & Pearl era |
| BREAK | 2015-2016 | Horizontal card, gold treatment |
| Delta (δ) | 2005-2006 | Wrong type Pokemon, δ in name |

### SPECIAL CARD TYPES:

**Shining Pokemon** (Neo era 2001-2002):
- "Shining" in name, entire card holographic, secret rare numbering (107/105)
- Sets: Neo Revelation, Neo Destiny

**Gold Star Pokemon** (EX era 2004-2007):
- Gold star (★) AFTER name, art breaks frame, shiny coloring
- Examples: Celebi ★ (ex14-100), Charizard ★ δ (ex15-100)

**Crystal Pokemon** (e-Card era 2002-2003):
- "Crystal" before name, holographic crystal pattern, different type
- Sets: Aquapolis, Skyridge

**Amazing Rare** (SWSH 2020):
- Rainbow/prismatic coloring, unique color pattern outside art box

### LANGUAGE DETECTION:
- Japanese: Katakana/Hiragana/Kanji text (カタカナ/ひらがな/漢字)
- Korean: Hangul characters (한글)
- Chinese: Traditional Chinese characters
- French/German/Italian/Spanish: Latin alphabet but non-English names

### JAPANESE CARDS (IMPORTANT):

**Japanese Rarity Types:**
- C, U, R, RR, SR, SAR, UR, AR, CHR, CSR, K, S, PR, SSR, HR

**Japanese Set Series (use these set_id prefixes):**
- Base/Jungle/Fossil era: base1-ja, jungle-ja, etc.
- Neo era: neo1-ja, neo2-ja, neo3-ja, neo4-ja
- e-Card: e1, e2, e3 (Expedition, Aquapolis, Skyridge)
- ADV Series (2003-2004): adv1, adv2, etc.
- PCG Series (2004-2006): pcg1, pcg2, etc.
- DP Series (2006-2010): dp1-ja, dp2-ja, etc.
- BW Series (2010-2013): bw1, bw2, etc.
- XY Series (2013-2016): xy1, xy2, etc.
- SM Series (2016-2019): sm1, sm2, etc.
- S Series (2019-2023): s1, s2, s3... s12 (Japanese Sword/Shield)
- SV Series (2023+): sv1, sv2, sv3... (Japanese Scarlet/Violet)

**CRITICAL: Card Number vs Pokedex Number**
- The NUMBER on Japanese cards is the CARD number in the set, NOT the Pokedex number
- Example: Japanese Base Set Articuno is card #18, NOT #144 (144 is Pokedex)
- Look for the actual printed number on the card (bottom right area)

**For Japanese cards, return:**
- set_name: Use JAPANESE set name if known (e.g., "ポケモンカード151" not "Pokemon Card 151")
- set_id: Use Japanese set code (e.g., "sv2a" for Pokemon Card 151)
`;

// Power Prompt - returns 3 candidates with confidence scores
const POWER_PROMPT = `You are a Pokemon TCG card identification expert. Use this reference guide:

${CARD_METADATA}

---

ANALYZE THIS CARD IMAGE:

1. Pokemon/Card NAME - exact text at top (in original language)
2. Card NUMBER - format like '120/130' or 'DP11'
3. SET SYMBOL - describe what you see (shape, color)
4. COPYRIGHT YEAR - usually at bottom
5. SPECIAL TYPE - Check guide above (Shining, Gold Star, Delta, LV.X, Prime, BREAK, TAG TEAM, etc.)
6. LANGUAGE - Detect: en, ja, ko, zh, fr, de, it, es, pt
7. If non-English, provide ENGLISH NAME for API lookup

Return JSON:

{
  "observed": {
    "name_text": "exact text you read (original language)",
    "name_english": "English name (if non-English card, otherwise same as name_text)",
    "number_text": "exact number you read",
    "set_symbol_description": "what the symbol looks like",
    "year": "copyright year",
    "special_type": "Shining/Gold Star/Delta/LV.X/Prime/BREAK/TAG TEAM/Amazing Rare/null",
    "language": "en/ja/ko/zh/fr/de/it/es/pt",
    "hp": "if visible",
    "type": "fire/water/psychic/etc"
  },
  "candidates": [
    {
      "rank": 1,
      "pokemon_name": "name with suffix (English)",
      "card_number": "XXX/YYY",
      "set_name": "full set name",
      "set_id": "tcgdex format like dp1, swsh4",
      "confidence": 0.95,
      "reasoning": "Why this is the best match"
    },
    {
      "rank": 2,
      "pokemon_name": "alternative if number/set wrong",
      "card_number": "XXX/YYY",
      "set_name": "set name",
      "set_id": "set id",
      "confidence": 0.60,
      "reasoning": "Why this could be right"
    },
    {
      "rank": 3,
      "pokemon_name": "fallback option",
      "card_number": "XXX/YYY",
      "set_name": "set name",
      "set_id": "set id",
      "confidence": 0.30,
      "reasoning": "Possible if something was misread"
    }
  ]
}

RULES:
- Use the SPECIAL CARD TYPES guide to identify niche cards
- Rank 1: Best match - all data aligns with guide
- Rank 2: Same name, different set OR nearby number (OCR error)
- Rank 3: Variant card (missed suffix) OR similar name
- For non-English cards, use English name in candidates for API lookup`; */

// Performance timing
const PERF = {
  scannerMount: null,
  cameraStartRequest: null,
  photosButtonTap: null,
  pickerClosed: null,
  analyzeButtonTap: null,
};

const Scanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { activeProfile, isViewerOnly } = useProfile();

  // Phase state
  const [phase, setPhase] = useState(PHASE.CAMERA);

  // Camera state
  const [mode, setMode] = useState('card');
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraFailed, setCameraFailed] = useState(false);
  const [error, setError] = useState(null);

  // Binders state
  const [binders, setBinders] = useState([]);
  const [selectedBinder, setSelectedBinder] = useState(null);
  const [showBinderPicker, setShowBinderPicker] = useState(false);

  // Available variants from API
  const [availableVariants, setAvailableVariants] = useState(['Normal']);
  const [isSaving, setIsSaving] = useState(false);

  // Capture state
  const [frozenFrame, setFrozenFrame] = useState(null);  // Immediate freeze
  const [originalCapture, setOriginalCapture] = useState(null); // Full-res original (for condition review)
  const [croppedImage, setCroppedImage] = useState(null); // Cropped to frame (high quality)
  const [isCapturing, setIsCapturing] = useState(false);

  // AI state
  const [analyzing, setAnalyzing] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);

  // Details state (variant/condition)
  const [variant, setVariant] = useState('normal');
  const [condition, setCondition] = useState('NM');
  const [gradeType, setGradeType] = useState('raw'); // 'raw' or 'graded'
  const [gradingCompany, setGradingCompany] = useState('PSA');
  const [tradingStatus, setTradingStatus] = useState('TRADE'); // 'PC' or 'TRADE'
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [showParentModal, setShowParentModal] = useState(false);

  // Helper to get grade options based on company
  const getGradeOptions = () => {
    if (gradingCompany === 'PSA') return PSA_GRADES;
    if (gradingCompany === 'CGC') return CGC_GRADES;
    return BGS_GRADES;
  };

  // Force PC for viewer-only users (kids without parent trade permission)
  useEffect(() => {
    if (isViewerOnly) {
      setTradingStatus('PC');
    }
  }, [isViewerOnly]);

  // Handle user-uploaded digital image (with community tracking)
  const handleImageUpload = async (imageDataUrl) => {
    if (!selectedCard || !user) return;

    setIsUploadingImage(true);
    try {
      // 1. Upload to Supabase storage
      const fileName = `user-uploads/${user.id}/${Date.now()}.jpg`;
      const base64Data = imageDataUrl.split(',')[1];
      const binaryData = atob(base64Data);
      const bytes = new Uint8Array(binaryData.length);
      for (let i = 0; i < binaryData.length; i++) {
        bytes[i] = binaryData.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'image/jpeg' });

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('card-images')
        .upload(fileName, blob, { contentType: 'image/jpeg', upsert: true });

      if (uploadError) {
        console.error('[Scanner] Image upload error:', uploadError);
        // Still use local image for preview
        setSelectedCard(prev => ({
          ...prev,
          image: imageDataUrl,
          imageHigh: imageDataUrl,
        }));
      } else {
        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('card-images')
          .getPublicUrl(fileName);

        console.log('[Scanner] Image uploaded:', publicUrl);

        // Update card with uploaded URL
        setSelectedCard(prev => ({
          ...prev,
          image: publicUrl,
          imageHigh: publicUrl,
          userUploadedImage: publicUrl,
        }));

        // 2. Record in ai_card_submissions for community review
        const submissionData = {
          user_id: user.id,
          name: selectedCard.name,
          set_name: selectedCard.setName,
          set_id: selectedCard.setCode,
          number: selectedCard.localId || selectedCard.number,
          digital_image_url: publicUrl,
          submission_type: 'image_upload', // Distinguish from AI chat submissions
          submission_status: 'pending_review',
          user_confirmed: true,
          created_at: new Date().toISOString(),
        };

        console.log('[Scanner] Recording image submission for review:', submissionData);
        const { error: submissionError } = await supabase
          .from('ai_card_submissions')
          .insert(submissionData);

        if (submissionError) {
          console.log('[Scanner] Submission record error (non-blocking):', submissionError);
          // Don't throw - image was uploaded successfully
        }
      }
    } catch (err) {
      console.error('[Scanner] Image upload error:', err);
      // Fallback to local preview
      setSelectedCard(prev => ({
        ...prev,
        image: imageDataUrl,
        imageHigh: imageDataUrl,
      }));
    }

    setIsUploadingImage(false);
    setShowImageUpload(false);
  };

  // Focus indicator state
  const [focusTap, setFocusTap] = useState(null); // { x, y } screen coords

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [previewImage, setPreviewImage] = useState(null); // For expanded image view
  const [expandedReasoning, setExpandedReasoning] = useState(null); // Which candidate's reasoning is expanded

  // Library image state (for cropping picked photos)
  const [libraryImage, setLibraryImage] = useState(null);
  const transformRef = useRef(null);

  // Refs
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);
  const cropCanvasRef = useRef(null);
  const frameRef = useRef(null);
  const pickCropFrameRef = useRef(null);
  const pickCropImgRef = useRef(null);

  // ==========================================
  // CAMERA LIFECYCLE
  // ==========================================

  const startCamera = useCallback(async () => {
    PERF.cameraStartRequest = Date.now();
    const sinceMount = PERF.scannerMount ? Date.now() - PERF.scannerMount : 0;
    console.log(`[PERF] Camera start requested (${sinceMount}ms since mount)`);

    try {
      setError(null);

      if (Capacitor.isNativePlatform()) {
        // Use CameraService - HARD RESET ensures full-screen dimensions
        await CameraService.show('scanner-camera-view');

        // Give native camera time to fully render with new dimensions
        // This prevents the "broken then fixed" flash
        await new Promise(r => setTimeout(r, 200));

        const cameraTime = Date.now() - PERF.cameraStartRequest;
        const totalTime = Date.now() - PERF.scannerMount;
        console.log(`[PERF] CAMERA READY: ${cameraTime}ms (total ${totalTime}ms since mount)`);
        setCameraReady(true);
      } else {
        // Web fallback - use getUserMedia
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setCameraReady(true);
        }
      }
    } catch (err) {
      console.error('Camera error:', err);
      setCameraFailed(true);
    }
  }, []);

  const stopCamera = useCallback(async () => {
    // Only stop if camera is actually ready
    if (!cameraReady) return;

    try {
      setCameraReady(false); // Set first to prevent multiple calls
      if (Capacitor.isNativePlatform()) {
        // Use CameraService to hide (keeps session alive)
        await CameraService.hide();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    } catch (e) {
      // Ignore "already stopped" errors
      if (!e?.message?.includes('already stopped')) {
        console.error('Stop camera error:', e);
      }
    }
  }, [cameraReady]);

  // Make body transparent for native camera preview (on mount/unmount only)
  useEffect(() => {
    PERF.scannerMount = Date.now();
    console.log('[PERF] ====== SCANNER MOUNTED ======');

    if (Capacitor.isNativePlatform()) {
      document.body.classList.add('camera-active');
      document.documentElement.style.backgroundColor = 'transparent';
    }
    return () => {
      document.body.classList.remove('camera-active');
      document.documentElement.style.backgroundColor = '';
    };
  }, []);

  // Handle incoming card data from AICardChat (details-only mode)
  useEffect(() => {
    const mode = location.state?.mode;
    const cardData = location.state?.cardData;
    const digitalImage = location.state?.digitalImage;
    const originalPhoto = location.state?.originalPhoto;

    if (mode === 'details-only' && cardData) {
      console.log('[Scanner] Received card data from AICardChat:', cardData);

      // Map AICardChat data format to Scanner format
      setSelectedCard({
        name: cardData.name,
        setName: cardData.set_name,
        setCode: cardData.set_id,
        number: cardData.number,
        tcgId: cardData.tcg_id,
        image: digitalImage || cardData.digital_image_url,
        imageHigh: digitalImage || cardData.digital_image_url,
        localId: cardData.number,
      });

      // Set cropped image if original photo was passed
      if (originalPhoto) {
        setCroppedImage(originalPhoto);
      }

      // Skip to DETAILS phase
      setPhase(PHASE.DETAILS);

      // Clear the location state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Fetch user's binders on mount
  useEffect(() => {
    const fetchBinders = async () => {
      const profileId = activeProfile?.id;
      console.log('[Scanner] Fetching binders for profile:', profileId);
      if (!profileId) {
        console.log('[Scanner] No profile ID, skipping binder fetch');
        return;
      }
      try {
        const { data, error } = await supabase
          .from('binders')
          .select('*')
          .eq('user_id', profileId)
          .order('name');

        console.log('[Scanner] Binders response:', { count: data?.length, error });

        if (!error && data) {
          setBinders(data);
          console.log('[Scanner] Loaded', data.length, 'binders');
        }
      } catch (e) {
        console.log('[Scanner] Failed to fetch binders:', e);
      }
    };
    fetchBinders();
  }, [activeProfile?.id]);

  // Start camera when in camera phase
  useEffect(() => {
    console.log('[Scanner] Phase/camera effect:', { phase, cameraReady });
    if (phase === PHASE.CAMERA && !cameraReady) {
      console.log('[Scanner] Starting camera from useEffect...');
      startCamera();
    }
  }, [phase, cameraReady, startCamera]);

  // Cleanup camera on unmount only
  useEffect(() => {
    return () => {
      if (Capacitor.isNativePlatform()) {
        // Hide camera when leaving Scanner (keeps session alive for next visit)
        CameraService.hide().catch(() => {});
      }
    };
  }, []);

  // ==========================================
  // CAPTURE - Freeze immediately, then crop
  // ==========================================

  const capturePhoto = async () => {
    if (isCapturing || !cameraReady) return;

    setIsCapturing(true);
    console.log('[Scanner] Starting capture...');

    // IMMEDIATELY freeze the UI - show processing overlay on top of camera
    // Camera keeps running in background for instant retake
    setFrozenFrame(null);  // Clear any old frame
    setPhase(PHASE.FROZEN);  // Switch to frozen view instantly

    try {
      let fullImageData;
      let captureFilePath = null;

      if (Capacitor.isNativePlatform()) {
        console.log('[Scanner] Native capture via CameraService...');
        // Use CameraService - it stays in READY state after capture (no restart needed!)
        // storeToFile: true means we get a file path
        const captureResult = await CameraService.capture();
        console.log('[Scanner] Capture result:', captureResult);

        // Handle both file path and base64 responses
        if (captureResult.filePath && captureResult.filePath.startsWith('file://')) {
          // Got a file path - convert to WebView-accessible URL
          captureFilePath = captureResult.filePath;
          // Capacitor.convertFileSrc() converts file:// to capacitor:// for WebView access
          fullImageData = Capacitor.convertFileSrc(captureResult.filePath);
          console.log('[Scanner] File URI:', captureFilePath);
          console.log('[Scanner] Converted for WebView:', fullImageData);
        } else if (captureResult.filePath) {
          // Got base64 (fallback for some CameraPreview versions)
          fullImageData = `data:image/jpeg;base64,${captureResult.filePath}`;
          console.log('[Scanner] Using base64 fallback');
        } else {
          throw new Error('No capture data returned');
        }

        console.log('[Scanner] Capture complete (camera still running in background!)');
        // DO NOT hide camera - it stays active for instant retake
      } else {
        console.log('[Scanner] Web capture...');
        const video = videoRef.current;
        const canvas = canvasRef.current;

        if (canvas && video) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          canvas.getContext('2d').drawImage(video, 0, 0);
          fullImageData = canvas.toDataURL('image/jpeg', 0.95);
        }
        await stopCamera();
      }

      if (!fullImageData) {
        throw new Error('No image data captured');
      }

      console.log('[Scanner] Got image, setting frozen frame...');
      setFrozenFrame(fullImageData);
      setOriginalCapture(fullImageData);  // Save full-res original for condition review

      // Crop to frame area
      console.log('[Scanner] Cropping to frame...');
      await cropToFrame(fullImageData, captureFilePath);
      console.log('[Scanner] Crop complete!');

    } catch (e) {
      console.error('[Scanner] Capture error:', e);
      setError('Capture failed: ' + e.message);
      setPhase(PHASE.CAMERA);
      setCameraReady(true); // Camera is still running
    }

    setIsCapturing(false);
  };

  // Store frame dimensions when visible (before capture)
  const [savedFrameRect, setSavedFrameRect] = useState(null);

  // Save frame rect whenever it changes
  useEffect(() => {
    if (phase === PHASE.CAMERA && frameRef.current) {
      const rect = frameRef.current.getBoundingClientRect();
      setSavedFrameRect({
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height,
      });
    }
  }, [phase, cameraReady]);

  // Crop full image to just the frame area (using CameraService coordinate mapping)
  const cropToFrame = async (fullImage, filePath = null) => {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        console.log('[Scanner] Image loaded for cropping:', img.width, 'x', img.height);

        // Update CameraService's sensor aspect ratio based on actual image
        CameraService.sensorAspect = img.width / img.height;
        console.log('[Scanner] Updated sensor aspect ratio:', CameraService.sensorAspect.toFixed(3));

        const cropCanvas = cropCanvasRef.current;
        if (!cropCanvas) {
          console.log('[Scanner] No crop canvas, using full image');
          setCroppedImage(fullImage);
          setPhase(PHASE.CROP);
          resolve();
          return;
        }

        try {
          const imgWidth = img.width;
          const imgHeight = img.height;

          // Use saved frame rect (actual CSS-rendered dimensions)
          const screenWidth = window.innerWidth;
          const screenHeight = window.innerHeight;
          const frameRect = savedFrameRect || {
            x: screenWidth * 0.125,
            y: screenHeight * 0.2,
            width: screenWidth * 0.75,
            height: screenWidth * 0.75 * (7/5),
          };

          console.log('[Scanner] Frame rect from overlay:', frameRect);

          // Use CameraService's unified coordinate mapping
          const cropRect = CameraService.getCropRect(frameRect, imgWidth, imgHeight);

          console.log('[Scanner] Crop rect from CameraService:', cropRect);

          // Set canvas to cropped size
          cropCanvas.width = cropRect.width;
          cropCanvas.height = cropRect.height;

          const ctx = cropCanvas.getContext('2d');
          ctx.drawImage(
            img,
            cropRect.x, cropRect.y, cropRect.width, cropRect.height,  // Source rect
            0, 0, cropRect.width, cropRect.height                      // Dest rect
          );

          const croppedData = cropCanvas.toDataURL('image/jpeg', 0.98);  // High quality for condition review
          console.log('[Scanner] Cropped image ready (98% quality)');
          setCroppedImage(croppedData);
          setPhase(PHASE.CROP);
          resolve();
        } catch (e) {
          console.error('[Scanner] Crop error:', e);
          setCroppedImage(fullImage);
          setPhase(PHASE.CROP);
          resolve();
        }
      };

      img.onerror = (e) => {
        console.error('[Scanner] Image load error:', e);
        setCroppedImage(fullImage);
        setPhase(PHASE.CROP);
        resolve();
      };

      setTimeout(() => {
        if (!img.complete) {
          console.log('[Scanner] Image load timeout, using full image');
          setCroppedImage(fullImage);
          setPhase(PHASE.CROP);
          resolve();
        }
      }, 5000);

      // Handle both file:// URIs and data: URIs
      img.src = fullImage;
    });
  };

  // ==========================================
  // IMAGE COMPRESSION
  // ==========================================

  // Compress image before sending to Gemini (saves upload time + tokens)
  const compressImage = (dataUrl, maxWidth = 1024, quality = 0.7) => {
    return new Promise((resolve) => {
      const startTime = Date.now();
      const img = new Image();
      img.onload = () => {
        // Calculate new dimensions
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        // Draw to canvas at reduced size
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Export as compressed JPEG
        const compressed = canvas.toDataURL('image/jpeg', quality);
        const originalSize = Math.round(dataUrl.length / 1024);
        const compressedSize = Math.round(compressed.length / 1024);

        console.log(`[Scanner] Image compressed: ${originalSize}KB → ${compressedSize}KB (${Math.round(compressedSize/originalSize*100)}%) in ${Date.now() - startTime}ms`);

        resolve(compressed);
      };
      img.onerror = () => resolve(dataUrl); // Fallback to original
      img.src = dataUrl;
    });
  };

  // ==========================================
  // AI ANALYSIS
  // ==========================================

  const analyzeCard = async () => {
    PERF.analyzeButtonTap = Date.now();
    console.log('[PERF] Analyze button tapped');

    setPhase(PHASE.ANALYZING);
    setAnalyzing(true);
    setError(null);

    try {
      // Compress image before sending
      const compressStart = Date.now();
      const compressedImage = await compressImage(croppedImage, 1024, 0.7);
      console.log(`[PERF] Image compression: ${Date.now() - compressStart}ms`);

      // Send to Titan Speed Scan
      const titanStart = Date.now();
      const matches = await titanIdentify(compressedImage, 5, 'en');
      console.log(`[PERF] Titan API response: ${Date.now() - titanStart}ms`);

      if (!matches || matches.length === 0) {
        console.log('[Scanner] No matches from Titan');
        setPhase(PHASE.NOT_FOUND);
        setAnalyzing(false);
        return;
      }

      // Progressive UI: show placeholders immediately
      console.log('[Scanner] ======== FETCHING IMAGES FOR', matches.length, 'CANDIDATES (Progressive) ========');
      const allStart = Date.now();

      const placeholders = matches.map((m) => ({
        name: m.name,
        originalName: '',
        language: 'en',
        setName: m.set_name,
        setCode: m.set_id,
        number: m.local_id,
        confidence: Math.round((m.similarity || 0) * 100),
        reasoning: null,
        image: null,
        imageHigh: null,
        tcgId: null,
        source: null,
        loading: true,
      }));

      setCandidates(placeholders);
      setPhase(PHASE.CANDIDATES);
      const totalAnalyzeTime = Date.now() - PERF.analyzeButtonTap;
      console.log(`[PERF] RESULTS VISIBLE: ${totalAnalyzeTime}ms total (from Analyze tap to UI)`);

      // Fetch card data progressively
      matches.forEach(async (m, idx) => {
        const cardStart = Date.now();
        const result = await findCard(
          m.name,
          m.local_id,
          m.set_name,
          m.set_id,
          'en',
          m.name
        );

        console.log(`[Scanner] Card ${idx + 1} loaded in ${Date.now() - cardStart}ms`);

        setCandidates(prev => {
          const updated = [...prev];
          updated[idx] = {
            name: m.name,
            originalName: '',
            language: result?.language || 'en',
            setName: result?.setName || m.set_name,
            setCode: result?.setId || m.set_id,
            number: result?.localId || m.local_id,
            confidence: Math.round((m.similarity || 0) * 100),
            reasoning: null,
            image: result?.image || null,
            imageHigh: result?.imageHigh || null,
            tcgId: result?.tcgId || null,
            lookupTime: result?.lookupTime || null,
            source: result?.source || null,
            loading: false,
          };
          return updated;
        });
      });

      console.log('[Scanner] ======== All lookups dispatched:', Date.now() - allStart, 'ms ========');
    } catch (err) {
      console.error('[Scanner] Analysis error:', err);
      setError('Analysis failed. Please try again.');
      setPhase(PHASE.CROP);
    }

    setAnalyzing(false);
  };

  // ==========================================
  // CARD SELECTION & DETAILS
  // ==========================================

  const selectCandidate = async (card) => {
    setSelectedCard(card);

    // User has selected a card - now we can hide the camera
    // This frees up resources for the details/save phase
    if (Capacitor.isNativePlatform()) {
      console.log('[Scanner] Card selected, hiding camera for details phase');
      CameraService.hide().catch(() => {});
    }

    // Fetch full card details from TCGDex to get available variants
    if (card.tcgId) {
      try {
        const apiEndpoint = getTCGdexEndpoint('en');
        const res = await fetch(`${apiEndpoint}/cards/${card.tcgId}`);
        if (res.ok) {
          const details = await res.json();
          if (details.variants) {
            const variants = [];
            if (details.variants.normal) variants.push('Normal');
            if (details.variants.holo) variants.push('Holo');
            if (details.variants.reverse) variants.push('Reverse Holo');
            if (details.variants.firstEdition) variants.push('1st Edition');
            if (details.variants.wPromo) variants.push('W Promo');
            setAvailableVariants(variants.length > 0 ? variants : ['Normal']);
            setVariant(variants[0] || 'Normal');
          } else {
            setAvailableVariants(['Normal']);
            setVariant('Normal');
          }
        }
      } catch (e) {
        console.log('[Scanner] Could not fetch variants:', e);
        setAvailableVariants(['Normal']);
        setVariant('Normal');
      }
    } else {
      setAvailableVariants(['Normal', 'Holo', 'Reverse Holo', '1st Edition']);
      setVariant('Normal');
    }

    setPhase(PHASE.DETAILS);
  };

  // Search for cards using TCGDex API (fast!)
  // Parse search query into name and optional card number
  // "Charizard 006" -> { name: "Charizard", number: "006" }
  // "Charizard 35/91" -> { name: "Charizard", number: "35" }
  // "Charizard VSTAR 006" -> { name: "Charizard VSTAR", number: "006" }
  // "Charizard" -> { name: "Charizard", number: null }
  // "h1" -> { name: null, number: "h1" }
  const parseSearchQuery = (raw) => {
    const q = raw.trim();
    // Match a card number at the end: digits, digits/digits, or letter-prefix like h1, TG15, SV034
    const numberPattern = /\s+((?:[a-zA-Z]{1,3})?\d+(?:\/\d+)?)$/;
    const match = q.match(numberPattern);
    if (match) {
      const numberRaw = match[1];
      const name = q.slice(0, match.index).trim();
      // Strip the /XX denominator for matching (35/91 -> 35)
      const number = numberRaw.split('/')[0];
      return { name: name || null, number };
    }
    // Check if the entire query is a number pattern (just "006" or "h1" or "35/91")
    if (/^(?:[a-zA-Z]{1,3})?\d+(?:\/\d+)?$/.test(q)) {
      return { name: null, number: q.split('/')[0] };
    }
    return { name: q, number: null };
  };

  // Check if a card number matches the search number
  // Handles: "006" matches "6", "35" matches "035", "h1" matches "H1", "TG15" matches "tg15"
  const numberMatches = (cardNumber, searchNumber) => {
    if (!cardNumber || !searchNumber) return false;
    const normalize = (n) => n.toString().toLowerCase().replace(/^0+/, '') || '0';
    // Also strip /XX from card number (some cards store "35/91")
    const cardNum = normalize(cardNumber.split('/')[0]);
    const searchNum = normalize(searchNumber);
    return cardNum === searchNum;
  };

  const searchCards = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    setSearchResults([]);

    try {
      const apiEndpoint = getTCGdexEndpoint('en');
      const { name, number } = parseSearchQuery(searchQuery);

      // Number-only search: search local DB for all cards with that number
      if (!name && number) {
        const localResults = searchLocalByNumber(number);
        if (localResults.length > 0) {
          setSearchResults(localResults);
        } else {
          setError(`No cards found with number ${number}.`);
        }
        setIsSearching(false);
        return;
      }

      // Need at least a name with 3+ chars to search
      if (!name || name.length < 3) {
        setError('Enter at least 3 characters for the card name.');
        setIsSearching(false);
        return;
      }

      const response = await fetch(
        `${apiEndpoint}/cards?name=${encodeURIComponent(name)}`
      );

      if (!response.ok) throw new Error('Search failed');

      let cards = await response.json();

      // If a number was provided, pre-filter before loading details
      // This avoids fetching details for cards we'll throw away
      if (number && cards.length > 0) {
        const filtered = cards.filter(c => {
          const cardNum = c.localId || c.id?.split('-').pop() || '';
          return numberMatches(cardNum, number);
        });
        // Use filtered if we got hits, otherwise fall back to all (in case localId wasn't populated)
        if (filtered.length > 0) cards = filtered;
      }

      if (cards && cards.length > 0) {
        // Load details in batches of 10
        const allResults = [];
        for (let i = 0; i < cards.length; i += 10) {
          const batch = cards.slice(i, i + 10);
          const batchResults = await Promise.all(
            batch.map(async (card) => {
              try {
                const detailRes = await fetch(`${apiEndpoint}/cards/${card.id}`);
                if (detailRes.ok) {
                  const details = await detailRes.json();
                  return {
                    name: details.name,
                    setName: details.set?.name || 'Unknown Set',
                    setCode: details.set?.id || card.id?.split('-')[0] || '',
                    number: details.localId || '',
                    image: details.image ? `${details.image}/low.webp` : null,
                    imageHigh: details.image ? `${details.image}/high.webp` : null,
                    tcgId: card.id,
                  };
                }
              } catch (e) {
                console.log('Failed to load card details:', card.id);
              }
              return {
                name: card.name,
                setName: card.id?.split('-')[0] || 'Unknown',
                setCode: card.id?.split('-')[0] || '',
                number: card.localId || '',
                image: card.image ? `${card.image}/low.webp` : null,
                imageHigh: card.image ? `${card.image}/high.webp` : null,
                tcgId: card.id,
              };
            })
          );
          allResults.push(...batchResults.filter(Boolean));
          setSearchResults([...allResults]);
        }

        // Final number filter on detailed results (in case pre-filter missed due to missing localId)
        if (number) {
          const finalFiltered = allResults.filter(r => numberMatches(r.number, number));
          if (finalFiltered.length > 0) {
            setSearchResults(finalFiltered);
          }
        }
      } else {
        setError('No cards found. Try a different search.');
      }
    } catch (err) {
      console.error('Search error:', err);
      setError('Search failed. Please try again.');
    }

    setIsSearching(false);
  };

  const selectSearchResult = (card) => {
    setSelectedCard(card);
    setVariant('normal');

    // User has selected a card - hide camera for details phase
    if (Capacitor.isNativePlatform()) {
      console.log('[Scanner] Search result selected, hiding camera');
      CameraService.hide().catch(() => {});
    }

    setPhase(PHASE.DETAILS);
  };

  const manualEntry = () => {
    // Create a blank card for manual entry
    setSelectedCard({
      name: '',
      setName: '',
      setCode: '',
      number: '',
      manual: true,
    });

    // User is entering manually - hide camera
    if (Capacitor.isNativePlatform()) {
      console.log('[Scanner] Manual entry, hiding camera');
      CameraService.hide().catch(() => {});
    }

    setPhase(PHASE.DETAILS);
  };

  const saveCard = async () => {
    if (!user?.id || !selectedCard) return;

    setIsSaving(true);
    setError(null);

    try {
      // 1. Upload scanned image to Supabase storage
      let uploadedImageUrl = null;
      if (croppedImage) {
        const base64Data = croppedImage.split(',')[1];
        const blob = await fetch(croppedImage).then(r => r.blob());
        const filename = `${user.id}/${Date.now()}-scan.jpg`;

        const { error: uploadError } = await supabase.storage
          .from('Pokemon Card Images')
          .upload(filename, blob, { contentType: 'image/jpeg' });

        if (!uploadError) {
          const { data: urlData } = supabase.storage
            .from('Pokemon Card Images')
            .getPublicUrl(filename);
          uploadedImageUrl = urlData?.publicUrl;
        }
      }

      // 2. Generate UUID for new card
      const cardId = crypto.randomUUID();
      const profileId = activeProfile?.id;

      if (!profileId) {
        throw new Error('No active profile');
      }

      // 3. Insert card into cards table (same as ScanCardModal)
      const cardData = {
        id: cardId,
        user_id: profileId,
        binder_id: selectedBinder?.id || null,
        card_id: selectedCard.tcgId || selectedCard.id || null,
        name: selectedCard.name,
        variant: variant,
        grade: condition, // Using condition as grade (raw card)
        original_image_url: uploadedImageUrl,
        digital_image_url: selectedCard.imageHigh || selectedCard.image || null,
        tcgdex_image: selectedCard.imageHigh || selectedCard.image || null,
        set_name: selectedCard.setName,
        set_number: selectedCard.localId || selectedCard.number || selectedCard.cardNumber || null,
        is_for_trade: isViewerOnly ? false : tradingStatus === 'TRADE',
        trading_status: isViewerOnly ? 'PC' : tradingStatus,
        traded: false,
        is_private: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      console.log('[Scanner] Saving card:', JSON.stringify(cardData, null, 2));

      const { error: insertError } = await supabase
        .from('cards')
        .insert(cardData);

      if (insertError) throw insertError;

      // 4. Link to binder if selected
      if (selectedBinder?.id) {
        console.log('[Scanner] Linking card to binder:', selectedBinder.id);
        const { error: binderError } = await supabase
          .from('card_binders')
          .insert({
            card_id: cardId,
            binder_id: selectedBinder.id,
          });
        if (binderError) {
          console.log('[Scanner] Binder link error:', binderError);
        } else {
          console.log('[Scanner] Card linked to binder successfully');
        }
      } else {
        console.log('[Scanner] No binder selected, skipping link');
      }

      // Success! Navigate back
      navigate('/cards', {
        state: { cardAdded: true, cardName: selectedCard.name }
      });

    } catch (err) {
      // Better error logging for Supabase errors
      const errorDetails = {
        message: err?.message || 'Unknown error',
        code: err?.code,
        details: err?.details,
        hint: err?.hint,
        full: JSON.stringify(err),
      };
      console.error('[Scanner] Save error:', errorDetails);
      setError(`Failed to save card: ${errorDetails.message}`);
    }

    setIsSaving(false);
  };

  // ==========================================
  // NAVIGATION
  // ==========================================

  const retake = async () => {
    setFrozenFrame(null);
    setCroppedImage(null);
    setLibraryImage(null);
    setCandidates([]);
    setSelectedCard(null);
    setError(null);

    // Camera container is now ALWAYS in DOM (just hidden)
    // Native preview should still be attached to it
    const cameraEl = document.getElementById('scanner-camera-view');
    console.log('[Scanner] Retake - camera container in DOM:', !!cameraEl);
    if (cameraEl) {
      const rect = cameraEl.getBoundingClientRect();
      console.log('[Scanner] Camera container rect:', { x: rect.x, y: rect.y, w: rect.width, h: rect.height });
    }

    // Camera is still running in background - just switch back to camera view
    // This provides instant retake without camera restart lag
    if (Capacitor.isNativePlatform() && CameraService.isReady()) {
      console.log('[Scanner] Instant retake - camera already running');
      const previewRect = CameraService.getPreviewRect();
      console.log('[Scanner] CameraService preview rect:', previewRect);
      setCameraReady(true);
    }

    setPhase(PHASE.CAMERA);
  };

  // Pick from photo library
  const pickFromPhotos = async () => {
    // Set PICKING phase FIRST to prevent useEffect from restarting camera
    PERF.photosButtonTap = Date.now();
    console.log('[PERF] Photos button tapped');
    setPhase(PHASE.PICKING);

    try {
      // Show dark background during transition (prevent white flash)
      document.body.style.backgroundColor = '#000';

      // Hide camera (keeps session alive for fast resume)
      if (cameraReady) {
        console.log('[Scanner] Hiding camera for photo picker...');
        setCameraReady(false);
        if (Capacitor.isNativePlatform()) {
          await CameraService.hide();
        }
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(t => t.stop());
          streamRef.current = null;
        }
        // Small delay for iOS UI transition
        await new Promise(r => setTimeout(r, 100));
      }

      console.log('[Scanner] Opening photo picker...');
      const photo = await Camera.getPhoto({
        quality: 95,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Photos,
      });

      PERF.pickerClosed = Date.now();
      const pickerTime = Date.now() - PERF.photosButtonTap;
      console.log(`[PERF] Picker closed with photo: ${pickerTime}ms`);

      if (photo && photo.dataUrl) {
        setLibraryImage(photo.dataUrl);
        setPhase(PHASE.PICK_CROP);
      } else {
        // No photo selected, restart camera
        console.log(`[PERF] No photo selected, returning to camera (${pickerTime}ms total)`);
        document.body.classList.add('camera-active');
        // Keep black background - React overlay will show loading state
        setCameraReady(false);
        setPhase(PHASE.CAMERA);
      }
    } catch (e) {
      PERF.pickerClosed = Date.now();
      const pickerTime = Date.now() - PERF.photosButtonTap;
      console.log(`[PERF] Picker cancelled/error after ${pickerTime}ms:`, e?.message || e);
      // User cancelled or error - restore and restart camera
      document.body.classList.add('camera-active');
      // Keep black background - React overlay will show loading state
      setCameraReady(false);
      setPhase(PHASE.CAMERA);
    }
  };

  // Crop the library image using current transform position
  const cropLibraryImage = async () => {
    if (!libraryImage) return;

    setPhase(PHASE.FROZEN);

    // Read rects BEFORE phase change unmounts the elements
    const imgEl = pickCropImgRef.current;
    const frameEl = pickCropFrameRef.current;
    const imgRect = imgEl ? imgEl.getBoundingClientRect() : null;
    const frameRect = frameEl ? frameEl.getBoundingClientRect() : null;

    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const cropCanvas = cropCanvasRef.current;
        if (!cropCanvas || !imgRect || !frameRect) {
          setCroppedImage(libraryImage);
          setFrozenFrame(libraryImage);
          setPhase(PHASE.CROP);
          resolve();
          return;
        }

        // getBoundingClientRect on the img already includes zoom/pan transforms
        // So we just map frame coords to image natural coords directly
        const scaleX = img.naturalWidth / imgRect.width;
        const scaleY = img.naturalHeight / imgRect.height;

        const srcX = (frameRect.left - imgRect.left) * scaleX;
        const srcY = (frameRect.top - imgRect.top) * scaleY;
        const srcWidth = frameRect.width * scaleX;
        const srcHeight = frameRect.height * scaleY;

        console.log('[Scanner] Library crop:', {
          imgRect: { left: imgRect.left, top: imgRect.top, width: imgRect.width, height: imgRect.height },
          frameRect: { left: frameRect.left, top: frameRect.top, width: frameRect.width, height: frameRect.height },
          natural: { width: img.naturalWidth, height: img.naturalHeight },
          src: { srcX, srcY, srcWidth, srcHeight },
        });

        // Clamp to image bounds
        const clampedX = Math.max(0, srcX);
        const clampedY = Math.max(0, srcY);
        const clampedW = Math.min(srcWidth, img.naturalWidth - clampedX);
        const clampedH = Math.min(srcHeight, img.naturalHeight - clampedY);

        cropCanvas.width = clampedW;
        cropCanvas.height = clampedH;

        const ctx = cropCanvas.getContext('2d');
        ctx.drawImage(
          img,
          clampedX, clampedY, clampedW, clampedH,
          0, 0, clampedW, clampedH
        );

        const croppedData = cropCanvas.toDataURL('image/jpeg', 0.98);
        setCroppedImage(croppedData);
        setFrozenFrame(croppedData);
        setOriginalCapture(libraryImage);
        setPhase(PHASE.CROP);
        resolve();
      };

      img.onerror = () => {
        setCroppedImage(libraryImage);
        setFrozenFrame(libraryImage);
        setPhase(PHASE.CROP);
        resolve();
      };

      img.src = libraryImage;
    });
  };

  const handleClose = async () => {
    await stopCamera();
    navigate(-1);
  };

  const goBack = () => {
    if (phase === PHASE.DETAILS) {
      // Go back to wherever we came from
      if (searchResults.length > 0) {
        setPhase(PHASE.SEARCH);
      } else if (candidates.length > 0) {
        setPhase(PHASE.CANDIDATES);
      } else {
        setPhase(PHASE.NOT_FOUND);
      }
    } else if (phase === PHASE.SEARCH) {
      setSearchQuery('');
      setSearchResults([]);
      setPhase(PHASE.NOT_FOUND);
    } else if (phase === PHASE.NOT_FOUND) {
      setPhase(PHASE.CANDIDATES);
    } else if (phase === PHASE.CANDIDATES) {
      setPhase(PHASE.CROP);
    } else if (phase === PHASE.CROP) {
      // If we came from library, go back to pick_crop, otherwise retake camera
      if (libraryImage) {
        setPhase(PHASE.PICK_CROP);
      } else {
        retake();
      }
    } else if (phase === PHASE.PICK_CROP) {
      setLibraryImage(null);
      setPhase(PHASE.CAMERA);
    }
  };

  // ==========================================
  // RENDER
  // ==========================================

  // Global close handler - exit scanner from any phase
  const handleGlobalClose = async () => {
    await stopCamera();
    navigate(-1);
  };

  return (
    <div style={styles.page}>
      {/* Hidden canvases for image processing */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      <canvas ref={cropCanvasRef} style={{ display: 'none' }} />

      {/* GLOBAL black loading overlay - prevents white flash during camera transitions */}
      {phase === PHASE.CAMERA && !cameraReady && !cameraFailed && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: '#000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}>
          <div style={styles.spinner} />
        </div>
      )}

      {/* Camera Failed Fallback UI */}
      {phase === PHASE.CAMERA && cameraFailed && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: '#000',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          zIndex: 9999,
        }}>
          <div style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 24,
          }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <line x1="1" y1="1" x2="23" y2="23" stroke="#f55" strokeWidth="2" />
            </svg>
          </div>
          <h2 style={{ color: '#fff', fontSize: 20, margin: '0 0 8px 0', textAlign: 'center' }}>
            Camera Unavailable
          </h2>
          <p style={{ color: '#888', fontSize: 14, margin: '0 0 32px 0', textAlign: 'center' }}>
            No worries! You can still add cards from your photo library.
          </p>
          <button
            onClick={pickFromPhotos}
            style={{
              padding: '16px 32px',
              backgroundColor: 'var(--accent)',
              border: 'none',
              borderRadius: 12,
              color: '#fff',
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
            Choose from Photos
          </button>
          <button
            onClick={() => navigate(-1)}
            style={{
              marginTop: 16,
              padding: '12px 24px',
              backgroundColor: 'transparent',
              border: '1px solid #444',
              borderRadius: 12,
              color: '#888',
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            Go Back
          </button>
        </div>
      )}

      {/* Global Close Button - visible on phases without their own close */}
      {(phase === PHASE.ANALYZING || phase === PHASE.CANDIDATES || phase === PHASE.DETAILS || phase === PHASE.NOT_FOUND || phase === PHASE.SEARCH) && (
        <button style={styles.globalCloseBtn} onClick={handleGlobalClose}>
          <CloseIcon />
        </button>
      )}

      {/* ========== PICKING PHASE (transitioning to photo picker) ========== */}
      {phase === PHASE.PICKING && (
        <div style={styles.pickingOverlay}>
          <div style={styles.spinner} />
        </div>
      )}

      {/* Camera container - ALWAYS in DOM so native preview stays attached */}
      <div
        id="scanner-camera-view"
        style={{
          ...styles.cameraArea,
          // Hide when not in camera phase, but keep in DOM
          visibility: phase === PHASE.CAMERA ? 'visible' : 'hidden',
          pointerEvents: phase === PHASE.CAMERA ? 'auto' : 'none',
        }}
      >
        {!Capacitor.isNativePlatform() && (
          <video ref={videoRef} style={styles.video} playsInline autoPlay muted />
        )}
      </div>

      {/* ========== CAMERA PHASE ========== */}
      {phase === PHASE.CAMERA && (
        <>
          {/* Frame Overlay - tap to focus (native plugin shows its own indicator) */}
          <div
            style={styles.frameOverlay}
            onTouchEnd={(e) => {
              if (!Capacitor.isNativePlatform() || !cameraReady) return;

              const touch = e.changedTouches[0];
              // Trigger focus - native plugin shows its own focus square
              CameraService.focusAtPoint(touch.clientX, touch.clientY);
            }}
          >
            <div
              ref={frameRef}
              style={{
                ...styles.frame,
                aspectRatio: mode === 'slab' ? '2/3' : '5/7',
              }}
            >
              <div style={{ ...styles.corner, top: 0, left: 0 }} />
              <div style={{ ...styles.corner, top: 0, right: 0, transform: 'rotate(90deg)' }} />
              <div style={{ ...styles.corner, bottom: 0, left: 0, transform: 'rotate(-90deg)' }} />
              <div style={{ ...styles.corner, bottom: 0, right: 0, transform: 'rotate(180deg)' }} />
            </div>
            <p style={styles.hint}>
              {mode === 'slab' ? 'Align full slab in frame' : 'Align card in frame'}
            </p>
            <p style={styles.hintSub}>Pinch to zoom</p>
          </div>

          {/* Top Bar */}
          <div style={styles.topBar}>
            <button style={styles.iconBtn} onClick={handleClose}>
              <CloseIcon />
            </button>
            <div style={styles.modeToggle}>
              <button
                style={{ ...styles.modeBtn, ...(mode === 'card' ? styles.modeBtnActive : {}) }}
                onClick={() => setMode('card')}
              >
                Card
              </button>
              <button
                style={{ ...styles.modeBtn, ...(mode === 'slab' ? styles.modeBtnActive : {}) }}
                onClick={() => setMode('slab')}
              >
                Slab
              </button>
            </div>
            <div style={{ width: 44 }} />
          </div>

          {/* Bottom Bar */}
          <div style={styles.bottomBar}>
            <button style={styles.photosBtn} onClick={pickFromPhotos}>
              <PhotosIcon />
              <span>Photos</span>
            </button>
            <button style={styles.captureBtn} onClick={capturePhoto} disabled={!cameraReady || isCapturing}>
              <div style={styles.captureBtnInner} />
            </button>
            <div style={{ width: 80 }} />
          </div>

          {/* Focus Indicator */}
          <AnimatePresence>
            {focusTap && (
              <motion.div
                key="focus-indicator"
                initial={{ scale: 1.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: 'fixed',
                  left: focusTap.x - 30,
                  top: focusTap.y - 30,
                  width: 60,
                  height: 60,
                  border: '2px solid #FFD700',
                  borderRadius: 8,
                  pointerEvents: 'none',
                  zIndex: 9999,
                }}
              />
            )}
          </AnimatePresence>
        </>
      )}

      {/* ========== FROZEN PHASE (processing) ========== */}
      {phase === PHASE.FROZEN && (
        <div style={styles.processingOverlay}>
          {frozenFrame && (
            <img src={frozenFrame} alt="Captured" style={styles.frozenImg} />
          )}
          <div style={styles.processingBadge}>
            <div style={styles.spinner} />
            <p style={styles.processingText}>Processing...</p>
          </div>
        </div>
      )}

      {/* ========== PICK_CROP PHASE (library image cropping) ========== */}
      {phase === PHASE.PICK_CROP && libraryImage && (
        <div style={styles.pickCropPage}>
          {/* Zoomable/pannable image */}
          <TransformWrapper
            ref={transformRef}
            initialScale={1}
            minScale={0.5}
            maxScale={4}
            centerOnInit={true}
            doubleClick={{ disabled: true }}
          >
            <TransformComponent
              wrapperStyle={styles.transformWrapper}
              contentStyle={styles.transformContent}
            >
              <img ref={pickCropImgRef} src={libraryImage} alt="Library" style={styles.pickCropImg} />
            </TransformComponent>
          </TransformWrapper>

          {/* Frame overlay (non-interactive) */}
          <div style={styles.pickCropOverlay}>
            <div
              ref={pickCropFrameRef}
              style={{
                ...styles.frame,
                aspectRatio: mode === 'slab' ? '2/3' : '5/7',
              }}
            >
              <div style={{ ...styles.corner, top: 0, left: 0 }} />
              <div style={{ ...styles.corner, top: 0, right: 0, transform: 'rotate(90deg)' }} />
              <div style={{ ...styles.corner, bottom: 0, left: 0, transform: 'rotate(-90deg)' }} />
              <div style={{ ...styles.corner, bottom: 0, right: 0, transform: 'rotate(180deg)' }} />
            </div>
            <p style={styles.hint}>Position card in frame</p>
            <p style={styles.hintSub}>Pinch to zoom, drag to move</p>
          </div>

          {/* Top Bar */}
          <div style={styles.topBar}>
            <button style={styles.iconBtn} onClick={() => { setLibraryImage(null); setPhase(PHASE.CAMERA); }}>
              <CloseIcon />
            </button>
            <div style={styles.modeToggle}>
              <button
                style={{ ...styles.modeBtn, ...(mode === 'card' ? styles.modeBtnActive : {}) }}
                onClick={() => setMode('card')}
              >
                Card
              </button>
              <button
                style={{ ...styles.modeBtn, ...(mode === 'slab' ? styles.modeBtnActive : {}) }}
                onClick={() => setMode('slab')}
              >
                Slab
              </button>
            </div>
            <div style={{ width: 44 }} />
          </div>

          {/* Bottom Bar */}
          <div style={styles.pickCropBottom}>
            <button style={styles.secondaryBtn} onClick={() => { setLibraryImage(null); setPhase(PHASE.CAMERA); }}>
              Cancel
            </button>
            <button style={styles.primaryBtn} onClick={cropLibraryImage}>
              <CheckIcon /> Crop
            </button>
          </div>
        </div>
      )}

      {/* ========== CROP PHASE ========== */}
      {phase === PHASE.CROP && (
        <div style={styles.reviewPage}>
          <div style={styles.reviewHeader}>
            <button style={styles.iconBtn} onClick={goBack}>
              <BackIcon />
            </button>
            <span style={styles.headerTitle}>Review Photo</span>
            <div style={{ width: 44 }} />
          </div>

          <div style={styles.imagePreview}>
            <img src={croppedImage} alt="Captured card" style={styles.previewImg} />
          </div>

          <div style={styles.reviewActions}>
            <button style={styles.secondaryBtn} onClick={retake}>
              <RetakeIcon /> Retake
            </button>
            <button style={styles.primaryBtn} onClick={analyzeCard}>
              <ScanIcon /> Analyze Card
            </button>
          </div>
        </div>
      )}

      {/* ========== ANALYZING PHASE ========== */}
      {phase === PHASE.ANALYZING && (
        <div style={styles.analyzingPage}>
          <div style={styles.analyzingContent}>
            <div style={styles.cardPreviewSmall}>
              <img src={croppedImage} alt="Card" style={styles.smallImg} />
            </div>
            <div style={styles.spinnerLarge} />
            <p style={styles.analyzingText}>Looking up card...</p>
            <p style={styles.analyzingSubtext}>Searching database</p>
          </div>
        </div>
      )}

      {/* ========== CANDIDATES PHASE ========== */}
      {phase === PHASE.CANDIDATES && (
        <div style={styles.candidatesPage}>
          <div style={styles.reviewHeader}>
            <button style={styles.iconBtn} onClick={goBack}>
              <BackIcon />
            </button>
            <span style={styles.headerTitle}>Select Card</span>
            <div style={{ width: 44 }} />
          </div>

          <div style={styles.candidatesContent}>
            <p style={styles.candidatesLabel}>Is this your card?</p>

            <div style={styles.candidatesList}>
              {candidates.map((card, idx) => (
                <div key={idx} style={styles.candidateCard}>
                  {/* Tappable image */}
                  <button
                    style={styles.candidateImgBtn}
                    onClick={() => card.imageHigh && setPreviewImage(card.imageHigh)}
                    disabled={card.loading}
                  >
                    {card.loading ? (
                      <div style={styles.candidateLoading}>
                        <div style={styles.spinnerSmall} />
                      </div>
                    ) : card.image ? (
                      <img src={card.image} alt={card.name} style={styles.candidateImg} />
                    ) : (
                      <div style={styles.candidateNoImg}>
                        <NoImageIcon />
                        <span style={{ fontSize: 10, color: '#666', marginTop: 4 }}>No Image</span>
                      </div>
                    )}
                    {card.image && !card.loading && (
                      <div style={styles.tapToZoom}><ZoomIcon /></div>
                    )}
                  </button>

                  {/* Card info - show more details when no image */}
                  <div style={styles.candidateInfo}>
                    <div style={styles.candidateHeader}>
                      <span style={{
                        ...styles.candidateName,
                        // Larger text when no image to help identify
                        ...(card.image ? {} : { fontSize: 16, fontWeight: 600 }),
                      }}>{card.name}</span>
                      {card.source === 'community' ? (
                        <div style={{
                          ...styles.confidenceBadge,
                          backgroundColor: '#8b5cf6',
                          fontSize: 9,
                          padding: '3px 6px',
                        }}>
                          Community
                        </div>
                      ) : (
                        <div style={{
                          ...styles.confidenceBadge,
                          backgroundColor: card.confidence >= 80 ? '#22c55e' :
                                           card.confidence >= 50 ? '#f59e0b' : '#666'
                        }}>
                          {card.confidence}%
                        </div>
                      )}
                    </div>
                    {/* Show original name if different from English */}
                    {card.originalName && card.originalName !== card.name && (
                      <span style={{ fontSize: 12, color: '#888', marginBottom: 2 }}>
                        {card.originalName}
                      </span>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{
                        ...styles.candidateSet,
                        // More prominent when no image
                        ...(card.image ? {} : { fontSize: 14, color: '#bbb' }),
                      }}>{card.setName}</span>
                      {/* Language badge - always show */}
                      {card.language && (
                        <span style={{
                          fontSize: 10,
                          padding: '2px 6px',
                          borderRadius: 4,
                          backgroundColor: card.language === 'en' ? '#666' : '#3b82f6',
                          color: '#fff',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                        }}>
                          {card.language === 'en' ? 'English' :
                           card.language === 'ja' ? 'Japanese' :
                           card.language === 'fr' ? 'French' :
                           card.language === 'de' ? 'German' :
                           card.language === 'it' ? 'Italian' :
                           card.language === 'es' ? 'Spanish' :
                           card.language === 'pt' ? 'Portuguese' :
                           card.language === 'ko' ? 'Korean' :
                           card.language === 'zh' ? 'Chinese' :
                           card.language.toUpperCase()}
                        </span>
                      )}
                    </div>
                    <span style={styles.candidateNumber}>#{card.number} {card.setCode && `(${card.setCode})`}</span>

                    {/* Expandable reasoning */}
                    {card.reasoning && (
                      <button
                        style={styles.reasoningToggle}
                        onClick={() => setExpandedReasoning(expandedReasoning === idx ? null : idx)}
                      >
                        <span>Details</span>
                        <ChevronIcon style={{
                          transform: expandedReasoning === idx ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s',
                        }} />
                      </button>
                    )}
                    {expandedReasoning === idx && card.reasoning && (
                      <span style={styles.candidateReasoning}>{card.reasoning}</span>
                    )}
                  </div>

                  {/* Select button */}
                  <button
                    style={styles.selectBtn}
                    onClick={() => selectCandidate(card)}
                  >
                    Select
                  </button>
                </div>
              ))}
            </div>

            {/* Disclaimer about missing images */}
            <p style={{
              fontSize: 12,
              color: '#666',
              textAlign: 'center',
              marginBottom: 12,
              padding: '0 16px',
            }}>
              Some cards don't have digital images yet. Help CardChase by adding one later!
            </p>

            <button style={styles.notFoundBtn} onClick={() => setPhase(PHASE.NOT_FOUND)}>
              Not my card
            </button>
          </div>

          {/* Image Preview Modal */}
          <AnimatePresence>
            {previewImage && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={styles.previewOverlay}
                onClick={() => setPreviewImage(null)}
              >
                <motion.img
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0.8 }}
                  src={previewImage}
                  alt="Card preview"
                  style={styles.previewImg}
                />
                <p style={styles.previewHint}>Tap anywhere to close</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* ========== NOT FOUND PHASE ========== */}
      {phase === PHASE.NOT_FOUND && (
        <div style={styles.notFoundPage}>
          <div style={styles.reviewHeader}>
            <button style={styles.iconBtn} onClick={goBack}>
              <BackIcon />
            </button>
            <span style={styles.headerTitle}>Card Not Found</span>
            <div style={{ width: 44 }} />
          </div>

          <div style={styles.notFoundContent}>
            <div style={styles.cardPreviewSmall}>
              <img src={croppedImage} alt="Card" style={styles.smallImg} />
            </div>

            <p style={styles.notFoundText}>
              We couldn't identify this card automatically.
            </p>

            <div style={styles.notFoundOptions}>
              <button style={styles.optionCard} onClick={() => setPhase(PHASE.SEARCH)}>
                <SearchIcon />
                <div style={styles.optionCardText}>
                  <span style={styles.optionCardTitle}>Search your card manually</span>
                  <span style={styles.optionCardDesc}>Search by card name</span>
                </div>
              </button>

              <button style={styles.optionCard} onClick={() => navigate('/add-card-manual', {
                state: {
                  photo: croppedImage || originalCapture,
                  fromScanner: true,
                }
              })}>
                <img src={ChaseHi} alt="Chase" style={styles.chaseOptionIcon} />
                <div style={styles.optionCardText}>
                  <span style={styles.optionCardTitle}>Search using Chase's Research Lab</span>
                  <span style={styles.optionCardDesc}>Get additional AI help</span>
                </div>
              </button>

              <button style={styles.optionCard} onClick={retake}>
                <RetakeIcon />
                <div style={styles.optionCardText}>
                  <span style={styles.optionCardTitle}>Retake your photo for better quality</span>
                  <span style={styles.optionCardDesc}>Try scanning again</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========== SEARCH PHASE ========== */}
      {phase === PHASE.SEARCH && (
        <div style={styles.searchPage}>
          <div style={styles.reviewHeader}>
            <button style={styles.iconBtn} onClick={goBack}>
              <BackIcon />
            </button>
            <span style={styles.headerTitle}>Search Card</span>
            <div style={{ width: 44 }} />
          </div>

          <div style={styles.searchContent}>
            <div style={styles.searchInputWrapper}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchCards()}
                placeholder="Enter card name..."
                style={styles.searchInput}
                autoFocus
              />
              <button
                style={styles.searchBtn}
                onClick={searchCards}
                disabled={isSearching || !searchQuery.trim()}
              >
                {isSearching ? <div style={styles.spinnerSmall} /> : <SearchIcon />}
              </button>
            </div>

            {searchResults.length > 0 && (
              <div style={styles.searchResults}>
                {searchResults.map((card, idx) => (
                  <div key={idx} style={styles.searchResultCard}>
                    {/* Tappable image - opens preview */}
                    <button
                      style={styles.searchImgBtn}
                      onClick={() => setPreviewImage(card.imageHigh || card.image)}
                    >
                      {card.image ? (
                        <img src={card.image} alt={card.name} style={styles.searchResultImg} />
                      ) : (
                        <div style={styles.searchNoImg}>
                          <NoImageIcon />
                        </div>
                      )}
                      <div style={styles.tapToZoom}>
                        <ZoomIcon />
                      </div>
                    </button>

                    {/* Card details */}
                    <div style={styles.searchResultInfo}>
                      <span style={styles.searchResultName}>{card.name}</span>
                      <span style={styles.searchResultSet}>{card.setName}</span>
                      <span style={styles.searchResultNumber}>#{card.number}</span>
                    </div>

                    {/* Select button */}
                    <button
                      style={styles.selectBtn}
                      onClick={() => selectSearchResult(card)}
                    >
                      Select
                    </button>
                  </div>
                ))}
              </div>
            )}

            {!isSearching && searchResults.length === 0 && searchQuery && (
              <p style={styles.searchHint}>Press search or enter to find cards</p>
            )}
          </div>

          {/* Image Preview Modal */}
          <AnimatePresence>
            {previewImage && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={styles.previewOverlay}
                onClick={() => setPreviewImage(null)}
              >
                <motion.img
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0.8 }}
                  src={previewImage}
                  alt="Card preview"
                  style={styles.previewImg}
                />
                <p style={styles.previewHint}>Tap anywhere to close</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* ========== DETAILS PHASE (variant/condition) ========== */}
      {phase === PHASE.DETAILS && selectedCard && (
        <div style={styles.detailsPage}>
          <div style={styles.reviewHeader}>
            <button style={styles.iconBtn} onClick={goBack}>
              <BackIcon />
            </button>
            <span style={styles.headerTitle}>Card Details</span>
            <div style={{ width: 44 }} />
          </div>

          <div style={styles.detailsContent}>
            {/* Card name and set info */}
            <div style={styles.cardNameSection}>
              <h3 style={styles.selectedCardName}>{selectedCard.name || 'Unknown Card'}</h3>
              <p style={styles.selectedCardSet}>
                {selectedCard.setName} {selectedCard.number && `#${selectedCard.number}`}
              </p>
            </div>

            {/* Side by side: Your scan vs Official image */}
            <div style={styles.imageComparison}>
              <div style={styles.imageBox}>
                <span style={styles.imageLabel}>Your Scan</span>
                <img src={croppedImage} alt="Scanned" style={styles.comparisonImg} />
              </div>
              <div style={styles.imageBox}>
                <span style={styles.imageLabel}>Official</span>
                {selectedCard.imageHigh || selectedCard.image ? (
                  <img
                    src={selectedCard.imageHigh || selectedCard.image}
                    alt="Official"
                    style={styles.comparisonImg}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div style={{
                  ...styles.noImagePlaceholder,
                  display: (selectedCard.imageHigh || selectedCard.image) ? 'none' : 'flex'
                }}>
                  <NoImageIcon />
                  <span>No image found</span>
                  <button
                    style={{
                      marginTop: 8,
                      padding: '8px 16px',
                      background: '#3b82f6',
                      border: 'none',
                      borderRadius: 6,
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                    onClick={() => setShowImageUpload(true)}
                  >
                    Add Digital Image
                  </button>
                </div>
              </div>
            </div>

            {/* Variant Selection */}
            <div style={styles.optionSection}>
              <label style={styles.optionLabel}>Variant</label>
              <div style={styles.optionGrid}>
                {availableVariants.map((v) => (
                  <button
                    key={v}
                    style={{
                      ...styles.optionBtn,
                      ...(variant === v ? styles.optionBtnActive : {}),
                    }}
                    onClick={() => setVariant(v)}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>

            {/* Grade Type Toggle */}
            <div style={styles.optionSection}>
              <label style={styles.optionLabel}>Grade Type</label>
              <div style={styles.gradeTypeToggle}>
                <button
                  style={{
                    ...styles.gradeTypeBtn,
                    ...(gradeType === 'raw' ? styles.gradeTypeBtnActive : {}),
                  }}
                  onClick={() => { setGradeType('raw'); setCondition('NM'); }}
                >
                  Raw
                </button>
                <button
                  style={{
                    ...styles.gradeTypeBtn,
                    ...(gradeType === 'graded' ? styles.gradeTypeBtnActive : {}),
                  }}
                  onClick={() => { setGradeType('graded'); setCondition('PSA 10'); }}
                >
                  Graded
                </button>
              </div>
            </div>

            {/* Grading Company (only for graded) */}
            {gradeType === 'graded' && (
              <div style={styles.optionSection}>
                <label style={styles.optionLabel}>Grading Company</label>
                <div style={styles.companyToggle}>
                  {['PSA', 'CGC', 'BGS'].map((company) => (
                    <button
                      key={company}
                      style={{
                        ...styles.companyBtn,
                        ...(gradingCompany === company ? styles.companyBtnActive : {}),
                      }}
                      onClick={() => {
                        setGradingCompany(company);
                        setCondition(`${company} 10`);
                      }}
                    >
                      {company}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Condition/Grade Selection */}
            <div style={styles.optionSection}>
              <label style={styles.optionLabel}>{gradeType === 'raw' ? 'Condition' : 'Grade'}</label>
              <div style={gradeType === 'graded' ? styles.gradeList : styles.optionGrid}>
                {gradeType === 'raw' ? (
                  RAW_CONDITIONS.map((c) => (
                    <button
                      key={c}
                      style={{
                        ...styles.optionBtn,
                        ...(condition === c ? styles.optionBtnActive : {}),
                      }}
                      onClick={() => setCondition(c)}
                    >
                      {c}
                    </button>
                  ))
                ) : (
                  getGradeOptions().map((opt) => (
                    <button
                      key={opt.value}
                      style={{
                        ...styles.gradeOption,
                        ...(condition === opt.value ? styles.gradeOptionActive : {}),
                      }}
                      onClick={() => setCondition(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Trading Status */}
            <div style={styles.optionSection}>
              <label style={styles.optionLabel}>Trading Status</label>
              <div style={styles.gradeTypeToggle}>
                <button
                  style={{
                    ...styles.gradeTypeBtn,
                    ...(tradingStatus === 'PC' ? styles.gradeTypeBtnActive : {}),
                  }}
                  onClick={() => setTradingStatus('PC')}
                >
                  Personal Collection
                </button>
                <button
                  style={{
                    ...styles.gradeTypeBtn,
                    ...(tradingStatus === 'TRADE' ? styles.gradeTypeBtnActive : {}),
                    ...(isViewerOnly ? { opacity: 0.4 } : {}),
                  }}
                  onClick={() => {
                    if (isViewerOnly) {
                      setShowParentModal(true);
                    } else {
                      setTradingStatus('TRADE');
                    }
                  }}
                >
                  For Trade
                </button>
              </div>
            </div>

            {/* Binder Selection */}
            <div style={styles.optionSection}>
              <label style={styles.optionLabel}>Add to Binder (Optional)</label>
              <button
                style={styles.binderSelector}
                onClick={() => setShowBinderPicker(true)}
              >
                <span>{selectedBinder ? selectedBinder.name : 'No binder selected'}</span>
                <ChevronIcon />
              </button>
            </div>

            <button
              style={{
                ...styles.saveBtn,
                opacity: isSaving ? 0.7 : 1,
              }}
              onClick={saveCard}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <div style={styles.spinnerSmall} /> Saving...
                </>
              ) : (
                <>
                  <CheckIcon /> Add to Collection
                </>
              )}
            </button>

            <button style={styles.wrongCardBtn} onClick={() => setPhase(PHASE.NOT_FOUND)}>
              Wrong card? Search or enter manually
            </button>
          </div>

          {/* Binder Picker Modal */}
          <AnimatePresence>
            {showBinderPicker && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={styles.pickerOverlay}
                onClick={() => setShowBinderPicker(false)}
              >
                <motion.div
                  initial={{ y: 100 }}
                  animate={{ y: 0 }}
                  exit={{ y: 100 }}
                  style={styles.pickerSheet}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div style={styles.pickerHeader}>
                    <span style={styles.pickerTitle}>Select Binder</span>
                    <button style={styles.pickerClose} onClick={() => setShowBinderPicker(false)}>
                      <CloseIcon />
                    </button>
                  </div>
                  <div style={styles.pickerOptions}>
                    <button
                      style={{
                        ...styles.pickerOption,
                        ...(selectedBinder === null ? styles.pickerOptionActive : {}),
                      }}
                      onClick={() => {
                        setSelectedBinder(null);
                        setShowBinderPicker(false);
                      }}
                    >
                      No binder
                    </button>
                    {binders.map((binder) => (
                      <button
                        key={binder.id}
                        style={{
                          ...styles.pickerOption,
                          ...(selectedBinder?.id === binder.id ? styles.pickerOptionActive : {}),
                        }}
                        onClick={() => {
                          setSelectedBinder(binder);
                          setShowBinderPicker(false);
                        }}
                      >
                        {binder.name}
                      </button>
                    ))}
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Image Upload Modal */}
          <AnimatePresence>
            {showImageUpload && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={styles.pickerOverlay}
                onClick={() => setShowImageUpload(false)}
              >
                <motion.div
                  initial={{ y: 100 }}
                  animate={{ y: 0 }}
                  exit={{ y: 100 }}
                  style={styles.pickerSheet}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div style={styles.pickerHeader}>
                    <span style={styles.pickerTitle}>Add Digital Image</span>
                    <button style={styles.pickerClose} onClick={() => setShowImageUpload(false)}>
                      <CloseIcon />
                    </button>
                  </div>
                  <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <p style={{ color: '#999', fontSize: 14, margin: 0 }}>
                      Upload a clean digital image of this card
                    </p>
                    {isUploadingImage ? (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 12,
                        padding: '20px',
                        color: '#999',
                      }}>
                        <div style={styles.spinnerSmall} />
                        Uploading image...
                      </div>
                    ) : (
                      <>
                        <label style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8,
                          padding: '16px 20px',
                          borderRadius: 12,
                          backgroundColor: '#0ea5e9',
                          color: '#fff',
                          fontSize: 16,
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}>
                          <PhotosIcon />
                          Choose from Photos
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;

                              try {
                                const reader = new FileReader();
                                reader.onload = async (event) => {
                                  await handleImageUpload(event.target.result);
                                };
                                reader.readAsDataURL(file);
                              } catch (err) {
                                console.error('Error processing image:', err);
                                setError('Failed to process image');
                              }
                            }}
                          />
                        </label>
                        {Capacitor.isNativePlatform() && (
                          <button
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 8,
                              padding: '16px 20px',
                              borderRadius: 12,
                              border: '1px solid #2a2a2a',
                              backgroundColor: '#1a1a1a',
                              color: '#fff',
                              fontSize: 16,
                              fontWeight: 600,
                            }}
                            onClick={async () => {
                              try {
                                const photo = await Camera.getPhoto({
                                  quality: 90,
                                  allowEditing: false,
                                  resultType: CameraResultType.DataUrl,
                                  source: CameraSource.Camera,
                                });

                                if (photo.dataUrl) {
                                  await handleImageUpload(photo.dataUrl);
                                }
                              } catch (err) {
                                console.error('Camera error:', err);
                              }
                            }}
                          >
                            <CameraIcon />
                            Take Photo
                          </button>
                        )}
                      </>
                    )}
                    {!isUploadingImage && (
                      <button
                        style={{
                          padding: '14px',
                          border: 'none',
                          backgroundColor: 'transparent',
                          color: '#666',
                          fontSize: 14,
                        }}
                        onClick={() => setShowImageUpload(false)}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Error Toast */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            style={styles.errorToast}
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Parent permission modal for viewer-only users */}
      <AddParentModal
        isOpen={showParentModal}
        onClose={() => setShowParentModal(false)}
        feature="trading"
      />
    </div>
  );
};

// ============================================
// ICONS
// ============================================

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const BackIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);

const RetakeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
  </svg>
);

const CheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ScanIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const SearchIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

const EditIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const NoImageIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
    <line x1="2" y1="2" x2="22" y2="22" />
  </svg>
);

const ZoomIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
    <path d="M11 8v6M8 11h6" />
  </svg>
);

const PhotosIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const ChevronIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CameraIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

// ============================================
// STYLES
// ============================================

const styles = {
  globalCloseBtn: {
    position: 'fixed',
    top: 'max(12px, env(safe-area-inset-top))',
    right: 12,
    zIndex: 1000,
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: 'rgba(0,0,0,0.6)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
  },
  pickingOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
  },
  page: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
    zIndex: 9999,
  },

  // Camera Phase
  cameraArea: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  frameOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    // pointerEvents default (auto) - captures taps for focus indicator
  },
  frame: {
    width: '75%',
    maxHeight: '60%',
    border: '3px solid rgba(255,255,255,0.8)',
    borderRadius: 12,
    position: 'relative',
  },
  corner: {
    position: 'absolute',
    width: 24,
    height: 24,
    borderLeft: '4px solid #0ea5e9',
    borderTop: '4px solid #0ea5e9',
  },
  hint: {
    marginTop: 16,
    color: '#fff',
    fontSize: 14,
    fontWeight: 500,
    textShadow: '0 1px 4px rgba(0,0,0,0.5)',
    margin: '16px 0 4px 0',
  },
  hintSub: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: 12,
    margin: 0,
    textShadow: '0 1px 4px rgba(0,0,0,0.5)',
  },
  topBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    padding: 'max(16px, env(safe-area-inset-top)) 16px 12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  iconBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(0,0,0,0.5)',
    border: 'none',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modeToggle: {
    display: 'flex',
    backgroundColor: 'rgba(0,0,0,0.5)',
    borderRadius: 24,
    padding: 4,
  },
  modeBtn: {
    padding: '10px 20px',
    borderRadius: 20,
    border: 'none',
    fontSize: 14,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.6)',
    backgroundColor: 'transparent',
  },
  modeBtnActive: {
    backgroundColor: '#0ea5e9',
    color: '#fff',
  },
  bottomBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: '20px 20px max(24px, env(safe-area-inset-bottom))',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  photosBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    padding: '10px 16px',
    borderRadius: 12,
    border: 'none',
    backgroundColor: 'rgba(0,0,0,0.5)',
    color: '#fff',
    fontSize: 11,
    fontWeight: 500,
  },
  captureBtn: {
    width: 72,
    height: 72,
    borderRadius: 36,
    border: '4px solid rgba(255,255,255,0.9)',
    backgroundColor: 'transparent',
    padding: 4,
  },
  captureBtnInner: {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    backgroundColor: '#fff',
  },

  // Pick Crop Phase
  pickCropPage: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#0a0a0a',
    overflow: 'hidden',
  },
  transformWrapper: {
    width: '100%',
    height: '100%',
  },
  transformContent: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pickCropImg: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
  pickCropOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  },
  pickCropBottom: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    gap: 12,
    padding: '20px 20px max(24px, env(safe-area-inset-bottom))',
  },

  // Processing Overlay
  processingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  frozenImg: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    opacity: 0.6,
  },
  processingBadge: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
    padding: '24px 32px',
    borderRadius: 16,
    backgroundColor: 'rgba(0,0,0,0.8)',
    backdropFilter: 'blur(8px)',
    zIndex: 1,
  },
  spinner: {
    width: 32,
    height: 32,
    border: '3px solid rgba(255,255,255,0.2)',
    borderTopColor: '#0ea5e9',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  processingText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: 500,
    margin: 0,
  },

  // Review/Crop Phase
  reviewPage: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#0a0a0a',
  },
  reviewHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 'max(16px, env(safe-area-inset-top)) 16px 16px',
  },
  headerTitle: {
    color: '#fff',
    fontSize: 17,
    fontWeight: 600,
  },
  imagePreview: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  previewImg: {
    maxWidth: '100%',
    maxHeight: '100%',
    borderRadius: 12,
    objectFit: 'contain',
  },
  reviewActions: {
    display: 'flex',
    gap: 12,
    padding: '16px 20px max(24px, env(safe-area-inset-bottom))',
  },
  secondaryBtn: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '16px 24px',
    borderRadius: 12,
    border: '1px solid #333',
    backgroundColor: '#1a1a1a',
    color: '#fff',
    fontSize: 16,
    fontWeight: 600,
  },
  primaryBtn: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '16px 24px',
    borderRadius: 12,
    border: 'none',
    backgroundColor: '#0ea5e9',
    color: '#fff',
    fontSize: 16,
    fontWeight: 600,
  },

  // Analyzing Phase
  analyzingPage: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    backgroundColor: '#0a0a0a',
  },
  analyzingContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 24,
  },
  cardPreviewSmall: {
    width: 120,
    height: 168,
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
  },
  smallImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  spinnerLarge: {
    width: 48,
    height: 48,
    border: '3px solid rgba(255,255,255,0.2)',
    borderTopColor: '#0ea5e9',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  analyzingText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 600,
    margin: 0,
  },
  analyzingSubtext: {
    color: '#666',
    fontSize: 14,
    margin: 0,
  },

  // Candidates Phase
  candidatesPage: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#0a0a0a',
  },
  candidatesContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: '16px 20px',
    gap: 16,
    overflowY: 'auto',
  },
  candidatesLabel: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 500,
    margin: 0,
    textAlign: 'center',
  },
  candidatesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    width: '100%',
  },
  candidateCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '14px',
    borderRadius: 12,
    border: '1px solid #2a2a2a',
    backgroundColor: '#1a1a1a',
    color: '#fff',
  },
  candidateHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 2,
  },
  candidateImgBtn: {
    position: 'relative',
    border: 'none',
    background: 'none',
    padding: 0,
    borderRadius: 6,
    overflow: 'hidden',
    flexShrink: 0,
  },
  candidateImg: {
    width: 70,
    height: 98,
    borderRadius: 6,
    objectFit: 'cover',
    display: 'block',
  },
  candidateNoImg: {
    width: 70,
    height: 98,
    borderRadius: 6,
    backgroundColor: '#252525',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#444',
  },
  candidateLoading: {
    width: 70,
    height: 98,
    borderRadius: 6,
    backgroundColor: '#1a1a1a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  candidateInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },
  candidateName: {
    fontSize: 15,
    fontWeight: 600,
    color: '#fff',
  },
  candidateSet: {
    fontSize: 13,
    color: '#888',
  },
  candidateNumber: {
    fontSize: 12,
    color: '#666',
  },
  reasoningToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginTop: 6,
    padding: 0,
    border: 'none',
    backgroundColor: 'transparent',
    color: '#0ea5e9',
    fontSize: 12,
    fontWeight: 500,
  },
  candidateReasoning: {
    fontSize: 12,
    color: '#888',
    marginTop: 6,
    lineHeight: 1.4,
    paddingLeft: 0,
    borderLeft: 'none',
  },
  candidateActions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  confidenceBadge: {
    padding: '4px 10px',
    borderRadius: 12,
    backgroundColor: '#0ea5e9',
    fontSize: 13,
    fontWeight: 600,
    color: '#fff',
  },
  notFoundBtn: {
    padding: '12px 24px',
    borderRadius: 8,
    border: 'none',
    backgroundColor: 'transparent',
    color: '#666',
    fontSize: 14,
    fontWeight: 500,
    marginTop: 8,
    textAlign: 'center',
  },

  // Details Phase
  detailsPage: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#0a0a0a',
  },
  detailsContent: {
    flex: 1,
    padding: '16px 20px',
    overflowY: 'auto',
  },
  cardNameSection: {
    textAlign: 'center',
    marginBottom: 20,
  },
  selectedCardName: {
    color: '#fff',
    fontSize: 20,
    fontWeight: 600,
    margin: '0 0 4px 0',
  },
  selectedCardSet: {
    color: '#666',
    fontSize: 14,
    margin: 0,
  },
  imageComparison: {
    display: 'flex',
    gap: 12,
    marginBottom: 24,
  },
  imageBox: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
  },
  imageLabel: {
    color: '#999',
    fontSize: 12,
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  comparisonImg: {
    width: '100%',
    aspectRatio: '5/7',
    borderRadius: 8,
    objectFit: 'cover',
    backgroundColor: '#1a1a1a',
  },
  noImagePlaceholder: {
    width: '100%',
    aspectRatio: '5/7',
    borderRadius: 8,
    backgroundColor: '#1a1a1a',
    border: '1px dashed #333',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    color: '#666',
    fontSize: 12,
  },
  optionSection: {
    marginBottom: 24,
  },
  optionLabel: {
    display: 'block',
    color: '#999',
    fontSize: 13,
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 12,
  },
  optionGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  optionBtn: {
    padding: '12px 20px',
    borderRadius: 8,
    border: '1px solid #2a2a2a',
    backgroundColor: '#1a1a1a',
    color: '#999',
    fontSize: 14,
    fontWeight: 500,
  },
  optionBtnActive: {
    borderColor: '#0ea5e9',
    backgroundColor: 'rgba(14, 165, 233, 0.15)',
    color: '#0ea5e9',
  },
  gradeTypeToggle: {
    display: 'flex',
    gap: 8,
  },
  gradeTypeBtn: {
    flex: 1,
    padding: '12px 16px',
    borderRadius: 8,
    border: '1px solid #2a2a2a',
    backgroundColor: '#1a1a1a',
    color: '#999',
    fontSize: 14,
    fontWeight: 500,
  },
  gradeTypeBtnActive: {
    borderColor: '#0ea5e9',
    backgroundColor: 'rgba(14, 165, 233, 0.15)',
    color: '#0ea5e9',
  },
  companyToggle: {
    display: 'flex',
    gap: 8,
  },
  companyBtn: {
    flex: 1,
    padding: '12px 16px',
    borderRadius: 8,
    border: '1px solid #2a2a2a',
    backgroundColor: '#1a1a1a',
    color: '#999',
    fontSize: 14,
    fontWeight: 600,
  },
  companyBtnActive: {
    borderColor: '#0ea5e9',
    backgroundColor: 'rgba(14, 165, 233, 0.15)',
    color: '#0ea5e9',
  },
  gradeList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  gradeOption: {
    padding: '14px 16px',
    borderRadius: 8,
    border: '1px solid #2a2a2a',
    backgroundColor: '#1a1a1a',
    color: '#999',
    fontSize: 14,
    fontWeight: 500,
    textAlign: 'left',
  },
  gradeOptionActive: {
    borderColor: '#0ea5e9',
    backgroundColor: 'rgba(14, 165, 233, 0.15)',
    color: '#0ea5e9',
  },
  saveBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    width: '100%',
    padding: '18px 24px',
    borderRadius: 12,
    border: 'none',
    backgroundColor: '#22c55e',
    color: '#fff',
    fontSize: 17,
    fontWeight: 600,
    marginTop: 32,
  },
  wrongCardBtn: {
    width: '100%',
    padding: '14px',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#666',
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 'max(24px, env(safe-area-inset-bottom))',
  },
  binderSelector: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 16px',
    borderRadius: 12,
    border: '1px solid #2a2a2a',
    backgroundColor: '#1a1a1a',
    color: '#fff',
    fontSize: 15,
  },
  pickerOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'flex-end',
    zIndex: 200,
  },
  pickerSheet: {
    width: '100%',
    maxHeight: '60%',
    backgroundColor: '#1a1a1a',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    overflow: 'hidden',
  },
  pickerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid #2a2a2a',
  },
  pickerTitle: {
    color: '#fff',
    fontSize: 17,
    fontWeight: 600,
  },
  pickerClose: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#333',
    border: 'none',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pickerOptions: {
    padding: '8px 0',
    maxHeight: 300,
    overflowY: 'auto',
  },
  pickerOption: {
    width: '100%',
    padding: '16px 20px',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#fff',
    fontSize: 16,
    textAlign: 'left',
  },
  pickerOptionActive: {
    backgroundColor: 'rgba(14, 165, 233, 0.15)',
    color: '#0ea5e9',
  },

  // Not Found Phase
  notFoundPage: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#0a0a0a',
  },
  notFoundContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '24px 20px',
    gap: 24,
  },
  notFoundText: {
    color: '#999',
    fontSize: 15,
    textAlign: 'center',
    margin: 0,
  },
  notFoundOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    width: '100%',
    marginTop: 8,
  },
  optionCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '18px 20px',
    borderRadius: 12,
    border: '1px solid #2a2a2a',
    backgroundColor: '#1a1a1a',
    color: '#fff',
    textAlign: 'left',
  },
  chaseOptionIcon: {
    width: 32,
    height: 45,
    objectFit: 'contain',
    flexShrink: 0,
  },
  optionCardText: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  optionCardTitle: {
    fontSize: 16,
    fontWeight: 600,
  },
  optionCardDesc: {
    fontSize: 13,
    color: '#666',
  },

  // Search Phase
  searchPage: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#0a0a0a',
  },
  searchContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: '16px 20px',
    gap: 16,
    overflowY: 'auto',
  },
  searchInputWrapper: {
    display: 'flex',
    gap: 8,
  },
  searchInput: {
    flex: 1,
    padding: '14px 16px',
    borderRadius: 12,
    border: '1px solid #2a2a2a',
    backgroundColor: '#1a1a1a',
    color: '#fff',
    fontSize: 16,
    outline: 'none',
  },
  searchBtn: {
    width: 52,
    height: 52,
    borderRadius: 12,
    border: 'none',
    backgroundColor: '#0ea5e9',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchResults: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  searchResultCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px',
    borderRadius: 12,
    border: '1px solid #2a2a2a',
    backgroundColor: '#1a1a1a',
    color: '#fff',
  },
  searchImgBtn: {
    position: 'relative',
    border: 'none',
    background: 'none',
    padding: 0,
    borderRadius: 6,
    overflow: 'hidden',
  },
  searchResultImg: {
    width: 60,
    height: 84,
    borderRadius: 6,
    objectFit: 'cover',
    display: 'block',
  },
  searchNoImg: {
    width: 60,
    height: 84,
    borderRadius: 6,
    backgroundColor: '#252525',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#444',
  },
  tapToZoom: {
    position: 'absolute',
    bottom: 4,
    right: 4,
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
  },
  searchResultInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    textAlign: 'left',
  },
  searchResultName: {
    fontSize: 15,
    fontWeight: 600,
    color: '#fff',
  },
  searchResultSet: {
    fontSize: 13,
    color: '#888',
  },
  searchResultNumber: {
    fontSize: 12,
    color: '#666',
  },
  selectBtn: {
    padding: '10px 16px',
    borderRadius: 8,
    border: 'none',
    backgroundColor: '#0ea5e9',
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  searchHint: {
    color: '#666',
    fontSize: 14,
    textAlign: 'center',
    marginTop: 20,
  },
  previewOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.95)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
    padding: 20,
  },
  previewImg: {
    maxWidth: '90%',
    maxHeight: '75%',
    borderRadius: 12,
    objectFit: 'contain',
  },
  previewHint: {
    color: '#666',
    fontSize: 14,
    marginTop: 20,
  },
  spinnerSmall: {
    width: 20,
    height: 20,
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },

  // Error Toast
  errorToast: {
    position: 'absolute',
    bottom: 100,
    left: 20,
    right: 20,
    padding: '14px 20px',
    borderRadius: 12,
    backgroundColor: '#ef4444',
    color: '#fff',
    fontSize: 14,
    fontWeight: 500,
    textAlign: 'center',
  },
};

// Add keyframes for spinner
if (typeof document !== 'undefined') {
  const existing = document.getElementById('scanner-keyframes');
  if (!existing) {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'scanner-keyframes';
    styleSheet.textContent = `
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(styleSheet);
  }
}

export default Scanner;
